import { css } from '@emotion/react';
import t from 'react-translate';
import {
  doubleSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  AutoMatchingRule,
  Expression,
} from 'redux/schemas/models/mentoring-program-matching-rules';
import {
  MentorshipProgramProfileQuestion,
  QuestionType,
  ProgramLevelQuestion,
  OrgLevelQuestion,
} from 'redux/schemas/models/mentoring-program-profile-questions';
import {
  almostBlack,
  dividerMedium,
  novoEdBlue,
  shadedWhite,
} from 'athena/styles/colors';
import { useAppDispatch } from 'redux/store';
import { updateMentorshipProgramAutoMatchingRule } from 'redux/actions/mentoring-program-matching-rules';
import { useContext, useState } from 'react';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { setSaveStatus } from 'redux/actions/mentoring-programs';
import { wrapThunkAction } from 'redux/utils';
import ClickableContainer from 'components/clickable-container';
import { config } from '@config/pendo.config.json';
import OptionsMenu from './options-menu';
import ImportanceLevelSlider from './importance-level-slider';
import { gridStyles } from './styles';
import MentoringProgramContext from '../../../../context';
import { getName } from '../../../../constants';
import InlineConfirmation from 'shared/components/inline-confirmation';
import MentoringQuestionsDisplay from './mentoring-questions-display';
import TimeZoneOffsetInput from 'shared/components/inputs/nv-time-zone-offset-input';
import { TIMEZONE_QUESTION_TEXT } from '../../../program-profile-setup/org-level';

const styles = css`
  ${gridStyles};
  border-bottom: 1px solid ${shadedWhite};
  position: relative;

  .matching-rule-details {
    gap: ${standardSpacing}px;
    .question-text {
      width: fit-content;
    }
    .expression-description {
      color: ${almostBlack};
    }
    .expressions {
      gap: 13px;
      .expression-value {
        cursor: pointer;
        height: ${doubleSpacing}px;
        color: ${almostBlack};
        border-radius: ${quarterSpacing}px;
        border: 1px solid ${dividerMedium};
        &.selected {
          font-weight: 600;
          border: 2px solid ${novoEdBlue};
          background-color: rgba(14, 114, 237, 0.05);
        }
      }
    }
  }
`;

type MatchingRuleRowProps = {
  rule: AutoMatchingRule;
  onDelete: (ruleId: number) => void;
};

const getQuestionText = (question: MentorshipProgramProfileQuestion) => {
  const { isNovoedQuestion, novoedQuestionName, questionType, profileQuestion } = question;
  let questionText = '';
  if (isNovoedQuestion) {
    questionText = getName(novoedQuestionName, t);
  } else {
    if (
      questionType === QuestionType.QUESTION
      && (profileQuestion as ProgramLevelQuestion).questionList
    ) {
      const { questionList } = profileQuestion as ProgramLevelQuestion;
      questionText = questionList?.questionText;
    }
    if (
      questionType === QuestionType.PROFILE_SETTING
      && (profileQuestion as OrgLevelQuestion)?.profileSetting
    ) {
      const { profileSetting } = profileQuestion as OrgLevelQuestion;
      questionText = profileSetting?.name;
    }
  }
  return questionText;
};

const MatchingRuleRow = ({ rule, onDelete }: MatchingRuleRowProps) => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const [showConfirmationDelete, setShowConfirmationDelete] = useState(false);
  const { id: mentorshipProgramId } = mentoringProgram || {};
  const translationBase =
    t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE
      .CONFIGURATION.ASSIGNED.MATCHING_RULES;
  const expressions = {
    [Expression.SIMILAR]: translationBase.LIST.EXPRESSION.TYPES.SIMILAR(),
    [Expression.DIFFERENT]: translationBase.LIST.EXPRESSION.TYPES.DIFFERENT(),
  };
  const {
    id: ruleId,
    expression,
    importanceLevel,
    mentorshipProgramProfileQuestion,
  } = rule;
  const isTimeZone = (
    mentorshipProgramProfileQuestion?.questionType === QuestionType.QUESTION &&
    (mentorshipProgramProfileQuestion?.profileQuestion as ProgramLevelQuestion)?.questionList?.questionText === TIMEZONE_QUESTION_TEXT
  );
  const questionText = isTimeZone ?
    t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.HEADERS.TIME_ZONE() :
    getQuestionText(mentorshipProgramProfileQuestion);
  const { menteeQuestion, mentorQuestion } = mentorshipProgramProfileQuestion || {};

  const handleChange = autoMatchingRule => {
    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
    wrapThunkAction(
      dispatch(
        updateMentorshipProgramAutoMatchingRule({
          mentorshipProgramId,
          ruleId,
          autoMatchingRule,
        }),
      ),
    )
      .then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
      });
  };

  return (
    <div css={styles} className='p-4'>
      <InlineConfirmation
        show={showConfirmationDelete}
        onCancel={() => setShowConfirmationDelete(false)}
        onConfirm={() => {
          onDelete(ruleId);
          setShowConfirmationDelete(false);
        }}
        title={translationBase.INLINE_CONFIRMATION.TITLE()}
        description={translationBase.INLINE_CONFIRMATION.DESCRIPTION(questionText)}
        cancelText={translationBase.INLINE_CONFIRMATION.CANCEL()}
        confirmText={translationBase.INLINE_CONFIRMATION.DELETE()}
      >
        <>
          <div className='d-flex flex-column matching-rule-details'>
            <MentoringQuestionsDisplay
              questionText={questionText}
              menteeQuestion={menteeQuestion}
              mentorQuestion={mentorQuestion}
            />
            <div className='expression-description'>
              {isTimeZone
                ? translationBase.LIST.EXPRESSION.TIME_ZONE_DESCRIPTION()
                : translationBase.LIST.EXPRESSION.DESCRIPTION()
              }
            </div>
            {isTimeZone ? (
              <TimeZoneOffsetInput
                value={Number(rule.expressionValue ?? 3)}
                onChange={(offset) => handleChange({
                  expression: 'range',
                  expressionValue: offset,
                  responseType: 'timezone',
                })}
                min={0}
                max={8}
              />
            ) : (
              <div className='d-flex expressions'>
                {Object.entries(expressions).map(([key, value]) => (
                  <ClickableContainer
                    key={key}
                    onClick={() => handleChange({ expression: key })}
                    className={`pl-2 pr-2 text-medium align-items-center expression-value ${
                      expression === key ? 'selected' : ''
                    }`}
                    data-qa={config.pendo.athena.mentorshipProgram.settings.matchingRules.updateMatchingRuleExpressionType}
                  >
                    {value}
                  </ClickableContainer>
                ))}
              </div>
            )}
          </div>
          <ImportanceLevelSlider level={importanceLevel} onChange={handleChange} />
          <OptionsMenu onDelete={() => setShowConfirmationDelete(true)} />
        </>
      </InlineConfirmation>
    </div>
  );
};

export default MatchingRuleRow;
